import type { ReactNode } from 'react';
import { Check, Moon, Phone, Sun } from 'lucide-react';
import { useTheme } from '@/app/theme';
import { links } from '@/lib/config';
import { LogoMark } from '@/components/brand';
import { Button } from '@/components/ui/button';
export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  const { dark, setPref } = useTheme();
  const themeLabel = dark ? 'Switch to light theme' : 'Switch to dark theme';

  return (
    <div className="auth-frame">
      <div className="auth-form-side">
        <a href={links.marketing} className="flex items-center gap-2 font-semibold text-base">
          <LogoMark className="size-8" />
          CallSync
        </a>
        <main id="main-content" tabIndex={-1}>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
          <div className="mt-7">{children}</div>
        </main>
        <footer>
          <a href={links.privacy}>Privacy</a>
          <a href={links.terms}>Terms</a>
          <a href={links.support}>Help</a>
          <Button
            variant="ghost"
            size="icon-sm"
            className="auth-theme-toggle"
            aria-label={themeLabel}
            title={themeLabel}
            aria-pressed={dark}
            onClick={() => setPref(dark ? 'light' : 'dark')}
          >
            {dark ? <Sun size={17} aria-hidden="true" /> : <Moon size={17} aria-hidden="true" />}
          </Button>
        </footer>
      </div>
      <aside className="auth-story">
        <p className="section-label">A workspace for your team’s calls</p>
        <h2>
          Every conversation.
          <br />A clearer picture.
        </h2>
        <p>See calls, listen to recordings and keep your team’s follow-ups in one place.</p>
        <div className="auth-call-preview">
          <div>
            <Phone size={18} />
            <span>
              Priya Sharma
              <strong>Incoming call · 4m 32s</strong>
            </span>
            <Check size={16} />
          </div>
          <hr />
          <p>Interested in a site visit next week. Shared pricing and marked for a callback.</p>
          <span className="tag">Follow up</span>
        </div>
        <ul>
          {[
            'Works with your team’s Android phones and iPhones',
            'Call history, recordings and notes together',
            'Clear privacy controls for personal numbers',
          ].map((t) => (
            <li key={t}>
              <Check size={14} />
              {t}
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
export function FormError({ children }: { children: ReactNode }) {
  return children ? (
    <div role="alert" className="auth-message auth-error">
      {children}
    </div>
  ) : null;
}
export function FormNotice({ children }: { children: ReactNode }) {
  return (
    <div role="status" className="auth-message">
      {children}
    </div>
  );
}
export function OrDivider() {
  return (
    <div className="auth-divider">
      <span />
      or
      <span />
    </div>
  );
}
